import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface LayoutState {
  sidebarCollapsed: boolean;
  sidebarPinned: boolean;
  quickSearchOpen: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSidebarPinned: (pinned: boolean) => void;
  setQuickSearchOpen: (open: boolean) => void;

  /**
   * Below the `sm` breakpoint the sidebar renders as a temporary overlay
   * drawer instead of a permanent icon rail (`Sidebar.tsx`) — this tracks
   * just that drawer's open/closed state. Deliberately separate from
   * `sidebarCollapsed` (a desktop icon-rail-width preference) and
   * deliberately NOT persisted below: a mobile overlay should always start
   * closed on load, not resume whatever it was left at.
   */
  mobileSidebarOpen: boolean;
  toggleMobileSidebar: () => void;
  setMobileSidebarOpen: (open: boolean) => void;

  /** Enterprise Navigation Overhaul — accordion sidebar: at most one top-level section open at a time. */
  expandedSectionId: string | null;
  setExpandedSectionId: (id: string | null) => void;

  /** Enterprise Navigation Overhaul — pinned nav-registry leaf ids, in display order (reorder = array reorder). */
  favoriteIds: string[];
  toggleFavorite: (id: string) => void;
  reorderFavorite: (id: string, direction: 'up' | 'down') => void;
}

export const useLayoutStore = create<LayoutState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      sidebarPinned: true,
      quickSearchOpen: false,
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setSidebarPinned: (pinned) => set({ sidebarPinned: pinned }),
      setQuickSearchOpen: (open) => set({ quickSearchOpen: open }),

      mobileSidebarOpen: false,
      toggleMobileSidebar: () => set((state) => ({ mobileSidebarOpen: !state.mobileSidebarOpen })),
      setMobileSidebarOpen: (open) => set({ mobileSidebarOpen: open }),

      expandedSectionId: null,
      setExpandedSectionId: (id) => set((state) => ({ expandedSectionId: state.expandedSectionId === id ? null : id })),

      favoriteIds: [],
      toggleFavorite: (id) =>
        set((state) => ({
          favoriteIds: state.favoriteIds.includes(id) ? state.favoriteIds.filter((existing) => existing !== id) : [...state.favoriteIds, id],
        })),
      reorderFavorite: (id, direction) =>
        set((state) => {
          const index = state.favoriteIds.indexOf(id);
          const swapWith = direction === 'up' ? index - 1 : index + 1;
          if (index === -1 || swapWith < 0 || swapWith >= state.favoriteIds.length) return state;
          const next = [...state.favoriteIds];
          [next[index], next[swapWith]] = [next[swapWith]!, next[index]!];
          return { favoriteIds: next };
        }),
    }),
    {
      name: 'nexao-layout',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        sidebarPinned: state.sidebarPinned,
        favoriteIds: state.favoriteIds,
      }),
    },
  ),
);
